/**
 * Einstellungen → Sicherheit: eigenes Passwort ändern.
 *
 * Bis 18.09.2026 stand hier ein Mockup ohne State und ohne Route — ein Klick
 * auf „Passwort aktualisieren" tat nichts, ohne Fehler und ohne Erfolg
 * (gemeldet als „Passwort ändern geht nicht mehr"). Jetzt angebunden an
 * PUT /v2/admin/me/password.
 *
 * Die Bestätigung steht direkt NEBEN dem Button, nicht als Toast weit weg —
 * gleiches Muster wie im Familienkonto-Wohnen-Formular.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { v2Account, v2ErrorMessages } from '../../lib/account';

export function PasswordChangeForm({ dark, isMobile }: { dark: boolean; isMobile: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const canSubmit = current.length > 0 && next.length > 0 && repeat.length > 0 && !saving;

  const submit = async () => {
    setOkMsg(null);
    setErrors([]);

    // Lokal vorprüfen, damit die Wiederholung nicht erst der Server abweist.
    if (next !== repeat) {
      setErrors([tr('settings.security.password_mismatch')]);
      return;
    }

    setSaving(true);
    try {
      const res = await v2Account.updatePassword({
        current_password: current,
        password: next,
        password_confirmation: repeat,
      });
      setCurrent(''); setNext(''); setRepeat('');
      setOkMsg(
        res.sessions_revoked > 0
          ? tr('settings.security.password_saved_sessions', { count: res.sessions_revoked })
          : tr('settings.security.password_saved'),
      );
    } catch (e) {
      // Feldgenaue Backend-Meldungen zeigen statt „status code 422".
      const msgs = v2ErrorMessages(e);
      setErrors(msgs.length > 0 ? msgs : [tr('settings.security.password_failed')]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
        <Field label={tr('settings.security.current_password')}>
          <Input
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={current}
            onChange={e => setCurrent(e.target.value)}
            dark={dark}
          />
        </Field>
        <div />
        <Field label={tr('settings.security.new_password')}>
          <Input
            type="password"
            autoComplete="new-password"
            placeholder={tr('settings.security.new_password_placeholder')}
            value={next}
            onChange={e => setNext(e.target.value)}
            dark={dark}
          />
        </Field>
        <Field label={tr('settings.security.repeat_password')}>
          <Input
            type="password"
            autoComplete="new-password"
            placeholder={tr('settings.security.new_password_placeholder')}
            value={repeat}
            onChange={e => setRepeat(e.target.value)}
            dark={dark}
          />
        </Field>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="primary" dark={dark} disabled={!canSubmit} onClick={() => void submit()}>
          {saving ? tr('settings.security.password_saving') : tr('settings.security.update_password')}
        </Button>
        {/* Bestätigung direkt neben dem Button */}
        {okMsg && (
          <span role="status" style={{ fontSize: 12, color: '#039855', fontWeight: 600 }}>{okMsg}</span>
        )}
        {errors.length === 0 && !okMsg && (
          <span style={{ fontSize: 11.5, color: t.muted }}>{tr('settings.security.password_hint')}</span>
        )}
      </div>

      {errors.length > 0 && (
        <div role="alert" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {errors.map((m, i) => (
            <span key={i} style={{ fontSize: 12, color: '#b42318' }}>{m}</span>
          ))}
        </div>
      )}
    </div>
  );
}
